import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { cn } from '@/lib/utils';
import { SliderInput } from '../shared/SliderInput';
import { MN } from '../shared/constants';
import { UploadProfilePreview } from '../UploadProfilePreview';
import FleetProfileEditor from '../FleetProfileEditor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { HubV2State, BaseMode } from '../hooks/useHubV2State';

const BASE_MODES: { id: BaseMode; icon: string; label: string; description: string }[] = [
  { id: 'piekanalyse', icon: '📈', label: 'Piekanalyse', description: 'Koppel opgeslagen analyse van de netaansluiting' },
  { id: 'upload', icon: '📂', label: 'Upload', description: 'Upload kwartierwaarden van het netverbruik' },
  { id: 'handmatig', icon: '📊', label: 'Handmatig', description: 'Schat het jaarlijks netverbruik' },
];

export default function StepProfiel({ state }: { state: HubV2State }) {
  const {
    // Base load
    baseMode, setBaseMode, baseDem, setBaseDem, baseIncludesEv, setBaseIncludesEv,
    selectedPiekId, setSelectedPiekId, piekAnalyses, piekProfile,
    uploadedProfiles, uploadErrors, uploadLoading, handleConsumptionUpload, clearConsumption,
    resolvedBaseDem, chpBase, csfBase,
    // EV
    evEnabled, setEvEnabled, evMode, setEvMode, evDem, setEvDem,
    fleetConfig, setFleetConfig, fleetDemMwh,
    resolvedEvDem, chpEv,
    // Combined
    resolvedDem,
  } = state;

  return (
    <div className="space-y-4">
      {/* ═══ SECTIE A: BASISPROFIEL ═══ */}
      <div className="p-5 bg-white rounded-xl border border-[#D9E2EC] shadow-sm">
        <div className="text-sm font-bold text-[#0B1F4A] mb-0.5">📊 Basisprofiel — Netverbruik</div>
        <div className="text-xs text-[#8899AA] mb-4">
          Het huidige energieverbruik op de netaansluiting. Dit is het startpunt van de simulatie.
        </div>

        {/* Mode cards */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {BASE_MODES.map((mode) => (
            <button
              key={mode.id}
              onClick={() => setBaseMode(mode.id)}
              className={cn(
                "p-4 rounded-lg border-2 text-left transition-all cursor-pointer",
                baseMode === mode.id
                  ? "border-[#00A3C4] bg-[#F0FDFA] shadow-sm"
                  : "border-[#D9E2EC] bg-white hover:border-[#8899AA]"
              )}
            >
              <div className="text-xl mb-1">{mode.icon}</div>
              <div className="text-xs font-bold text-[#0B1F4A]">{mode.label}</div>
              <div className="text-[10px] text-[#8899AA] mt-0.5 leading-snug">{mode.description}</div>
            </button>
          ))}
        </div>

        {/* PIEKANALYSE */}
        {baseMode === 'piekanalyse' && (
          <div className="space-y-3">
            {!piekAnalyses?.length ? (
              <div className="text-[10px] text-muted-foreground bg-amber-50 rounded-md border border-amber-200 px-3 py-2 leading-relaxed">
                ⚠️ Geen piekanalyses gevonden. Koppel een organisatie aan de simulatie en upload verbruiksdata via <strong>Analyses → Piekvermogen</strong>.
              </div>
            ) : (
              <>
                <Select value={selectedPiekId ?? ''} onValueChange={(v) => setSelectedPiekId(v || null)}>
                  <SelectTrigger className="text-xs h-9">
                    <SelectValue placeholder="Selecteer piekanalyse..." />
                  </SelectTrigger>
                  <SelectContent>
                    {piekAnalyses.map((a: any) => (
                      <SelectItem key={a.id} value={a.id} className="text-xs">
                        {a.name} {a.analysis_year ? `(${a.analysis_year})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {piekProfile && (
                  <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] font-bold text-foreground">✅ Basisprofiel geladen</span>
                      <button onClick={() => setSelectedPiekId(null)}
                        className="text-[10px] text-destructive hover:text-destructive/80 cursor-pointer">✕ Loskoppelen</button>
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      ⚡ Jaarlijks netverbruik: <strong className="text-foreground">{piekProfile.annualMwh.toFixed(0)} MWh</strong>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* UPLOAD */}
        {baseMode === 'upload' && (
          <div className="space-y-3">
            <div className="text-[10px] text-muted-foreground bg-muted/50 rounded-md border border-border px-3 py-2 leading-relaxed">
              📂 Upload kwartierwaarden van de netaansluiting: twee kolommen met <strong>tijdstempel</strong> en <strong>kW-waarde</strong>.
            </div>
            {!uploadedProfiles.consumptionProfile ? (
              <div>
                <input type="file" accept=".csv,.txt,.xlsx,.xls" id="hub-v2-base-upload" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleConsumptionUpload(f); e.target.value = ''; }} />
                <button onClick={() => document.getElementById('hub-v2-base-upload')?.click()}
                  disabled={uploadLoading.consumption}
                  className="px-4 py-2 text-[11px] font-semibold rounded-lg border border-[#D9E2EC] bg-white text-[#0B1F4A] hover:bg-[#F5F8FB] cursor-pointer transition-colors disabled:opacity-50">
                  {uploadLoading.consumption ? '⏳ Verwerken...' : '📂 Kies bestand'}
                </button>
              </div>
            ) : (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-bold text-foreground">✅ Basisprofiel geladen</span>
                  <button onClick={clearConsumption} className="text-[10px] text-destructive hover:text-destructive/80 cursor-pointer">✕ Verwijderen</button>
                </div>
                {uploadedProfiles.consumptionStats && (
                  <div className="text-[10px] text-muted-foreground space-y-0.5">
                    <div>📊 <strong>{uploadedProfiles.consumptionStats.rows.toLocaleString('nl-NL')}</strong> datapunten</div>
                    <div>📅 {uploadedProfiles.consumptionStats.dateStart} t/m {uploadedProfiles.consumptionStats.dateEnd}</div>
                    <div>⚡ Jaarverbruik: <strong className="text-foreground">{uploadedProfiles.consumptionDemandMwh?.toFixed(0)} MWh</strong></div>
                  </div>
                )}
                {uploadedProfiles.consumptionProfile && (
                  <UploadProfilePreview data={uploadedProfiles.consumptionProfile} label="24-uurs netverbruiksprofiel (relatief)" color="hsl(var(--primary))" />
                )}
              </div>
            )}
            {uploadErrors.consumption && (
              <div className="text-[10px] text-destructive bg-destructive/10 rounded-lg px-3 py-2 border border-destructive/20">❌ {uploadErrors.consumption}</div>
            )}
          </div>
        )}

        {/* HANDMATIG */}
        {baseMode === 'handmatig' && (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2">
            <SliderInput label="Jaarlijks netverbruik" value={baseDem} set={setBaseDem} min={100} max={40000} step={100} unit="MWh" group="vraag" tooltip="Totaal jaarverbruik op de netaansluiting" />
          </div>
        )}

        {/* Checkbox: bevat deze data al EV-laden? */}
        {(baseMode === 'piekanalyse' || baseMode === 'upload') && (
          <label className="flex items-center gap-2 mt-3 cursor-pointer">
            <input type="checkbox" checked={baseIncludesEv} onChange={() => setBaseIncludesEv(!baseIncludesEv)}
              className="w-3.5 h-3.5 rounded border-[#D9E2EC] accent-[#00A3C4] cursor-pointer" />
            <span className="text-[10px] text-[#3D4F6F]">Deze data bevat al EV-laadverbruik</span>
          </label>
        )}

        {/* Basis badge */}
        <div className="flex items-center gap-2 mt-3 p-2 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
          <span className="text-[10px] text-[#8899AA]">Basisprofiel:</span>
          <span className="text-xs font-bold text-[#0B1F4A]">{resolvedBaseDem.toFixed(0)} MWh/j</span>
        </div>
      </div>

      {/* ═══ SECTIE B: EV-LAADVRAAG ═══ */}
      <div className="p-5 bg-white rounded-xl border border-[#D9E2EC] shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-bold text-[#0B1F4A]">🔌 EV-laadvraag</div>
            <div className="text-xs text-[#8899AA]">Extra belasting door elektrische voertuigen bovenop het basisprofiel</div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={evEnabled} onChange={() => setEvEnabled(!evEnabled)}
              className="w-4 h-4 rounded border-[#D9E2EC] accent-[#00A3C4] cursor-pointer" />
            <span className="text-xs font-semibold text-[#3D4F6F]">{evEnabled ? 'Actief' : 'Uit'}</span>
          </label>
        </div>

        {baseIncludesEv && evEnabled && (
          <div className="mb-3 text-[10px] text-amber-700 bg-amber-50 rounded-md border border-amber-200 px-3 py-2 leading-relaxed">
            ⚠️ <strong>Let op:</strong> Je hebt aangegeven dat het basisprofiel al EV-laden bevat. Extra EV-laadvraag hier wordt bovenop het basisprofiel opgeteld. Tel niet dubbel.
          </div>
        )}

        {evEnabled && (
          <div className="space-y-3">
            {/* EV mode toggle */}
            <div className="flex gap-2">
              <button onClick={() => setEvMode('vloot')}
                className={cn("px-3 py-1.5 text-[10px] font-semibold rounded-md border cursor-pointer transition-all",
                  evMode === 'vloot' ? "bg-[#0B1F4A] text-white border-[#0B1F4A]" : "bg-white text-[#3D4F6F] border-[#D9E2EC] hover:bg-[#F5F8FB]")}>
                🚛 Vlootprofiel
              </button>
              <button onClick={() => setEvMode('handmatig')}
                className={cn("px-3 py-1.5 text-[10px] font-semibold rounded-md border cursor-pointer transition-all",
                  evMode === 'handmatig' ? "bg-[#0B1F4A] text-white border-[#0B1F4A]" : "bg-white text-[#3D4F6F] border-[#D9E2EC] hover:bg-[#F5F8FB]")}>
                📊 Handmatig
              </button>
            </div>

            {evMode === 'vloot' && (
              <>
                <div className="text-[10px] text-muted-foreground">
                  Configureer de vloot per voertuigtype. Berekend EV-verbruik: <strong>{fleetDemMwh.toFixed(0)} MWh/j</strong>
                </div>
                <FleetProfileEditor config={fleetConfig} onChange={setFleetConfig} />
              </>
            )}

            {evMode === 'handmatig' && (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2">
                <SliderInput label="EV-laadvraag" value={evDem} set={setEvDem} min={0} max={30000} step={100} unit="MWh" group="vraag" tooltip="Jaarlijkse extra EV-laadvraag" />
              </div>
            )}
          </div>
        )}

        {/* EV badge */}
        <div className="flex items-center gap-2 mt-3 p-2 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
          <span className="text-[10px] text-[#8899AA]">EV-laadvraag:</span>
          <span className="text-xs font-bold text-[#0B1F4A]">{resolvedEvDem.toFixed(0)} MWh/j</span>
        </div>
      </div>

      {/* ═══ TOTAAL + PROFIEL PREVIEW ═══ */}
      <div className="p-4 bg-[#0B1F4A] rounded-xl text-white">
        <div className="flex items-center justify-between">
          <div className="text-xs">Totale locatievraag</div>
          <div className="text-lg font-bold">{resolvedDem.toFixed(0)} MWh/j</div>
        </div>
        <div className="flex gap-4 mt-1 text-[10px] opacity-70">
          <span>Basis: {resolvedBaseDem.toFixed(0)} MWh</span>
          <span>+</span>
          <span>EV: {resolvedEvDem.toFixed(0)} MWh</span>
        </div>
      </div>

      {/* CHP + CSF preview */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 bg-white rounded-xl border border-[#D9E2EC] shadow-sm">
          <div className="text-[11px] font-bold text-[#0B1F4A] mb-2">🕐 24-uurs profiel (basis)</div>
          <ResponsiveContainer width="100%" height={100}>
            <BarChart data={chpBase.map((v: number, i: number) => ({ h: `${i}h`, v: Math.round(v * 100) / 100 }))}>
              <XAxis dataKey="h" tick={{ fontSize: 7 }} interval={3} />
              <YAxis hide />
              <Tooltip />
              <Bar dataKey="v" fill="#00A3C4" radius={[2, 2, 0, 0]} barSize={6} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="p-4 bg-white rounded-xl border border-[#D9E2EC] shadow-sm">
          <div className="text-[11px] font-bold text-[#0B1F4A] mb-2">📅 Seizoensprofiel (basis)</div>
          <ResponsiveContainer width="100%" height={100}>
            <BarChart data={csfBase.map((v: number, i: number) => ({ m: MN[i], v: Math.round(v * 100) / 100 }))}>
              <XAxis dataKey="m" tick={{ fontSize: 7 }} />
              <YAxis hide />
              <Tooltip />
              <Bar dataKey="v" fill="#0B1F4A" radius={[2, 2, 0, 0]} barSize={10} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
